/**
 * PdfEmojiTextBuilder
 * --------------------
 * Splits text into Roboto (normal) and Emoji font segments for pdfmake.
 * Use this in your PDF definitions: e.g. { text: builder.make('Hello 🌍') }
 */
export class PdfEmojiTextBuilder {
  private readonly emojiRegex = /\p{Extended_Pictographic}/u; // matches single pictographic code point
  private readonly emojiAssistRegex = /[\u{200d}\u{fe0f}\u{20e3}\u{1f3fb}-\u{1f3ff}]/u; // joiners, VS, tones, keycap
  private readonly regionalIndicatorRegex = /[\u{1f1e6}-\u{1f1ff}]/u;
  private readonly segmenter: Intl.Segmenter | null =
    typeof Intl !== 'undefined' && typeof Intl.Segmenter !== 'undefined'
      ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
      : null;

  /**
   * Convert a plain string into an array of pdfmake text fragments,
   * each with the correct font (Roboto or Emoji).
   */
  make(text: string, baseStyle: any = {}): any[] {
    const fragments: any[] = [];
    if (!text) {
      return [
        {
          ...baseStyle,
          text: '',
          font: baseStyle.font || 'Roboto',
        },
      ];
    }

    const baseFont = baseStyle.font || 'Roboto';
    const segments = this.segmenter
      ? Array.from(this.segmenter.segment(text)).map((seg: Intl.SegmentData) => seg.segment)
      : Array.from(text);
    let buffer = '';

    const flushBuffer = () => {
      if (!buffer) return;
      fragments.push({
        ...baseStyle,
        text: buffer,
        font: baseFont,
      });
      buffer = '';
    };

    for (const segment of segments) {
      if (this.isEmojiSegment(segment)) {
        flushBuffer();
        fragments.push({
          ...baseStyle,
          text: segment,
          font: 'Emoji',
        });
      } else {
        buffer += segment;
      }
    }
    flushBuffer();

    return fragments;
  }

  /**
   * Convenience: wrap multiple lines into a pdfmake "content" array.
   * Each string becomes a paragraph with emoji-smart text fragments.
   */
  paragraphs(lines: string[], baseStyle: any = {}): any[] {
    return lines.map((line) => ({
      text: this.make(line, baseStyle),
      margin: baseStyle.margin || [0, 2],
    }));
  }

  private isEmojiSegment(segment: string): boolean {
    if (!segment) return false;
    if (this.emojiRegex.test(segment)) return true;
    if (segment.length > 1 && (this.emojiAssistRegex.test(segment) || this.regionalIndicatorRegex.test(segment))) {
      return true;
    }
    return false;
  }
}
